import type { JSONContent } from '@tiptap/core';
import type { Ref } from 'vue';
import type { UserInvitationAttributes } from '#layers/access/utils/extensions/user-invitation';
import type { MonospaceUser } from '#layers/data-model/public';
import { isValidEmail } from '#layers/access/utils/email';
import { isUserInvitation } from '#layers/access/utils/extensions/user-invitation';

export type InvitableUser = Pick<MonospaceUser, 'id' | 'email' | 'fullName'>;

export interface UseInvitationsOptions {
	invitations: Ref<UserInvitationAttributes[]>;
}

export function useInvitations(options: UseInvitationsOptions) {
	const { invitations } = options;

	function toEditorContent(): JSONContent {
		if (invitations.value.length === 0) {
			return { type: 'doc', content: [] };
		}

		const content: object[] = [];

		for (const invitation of invitations.value) {
			content.push({ type: 'invitation', attrs: invitation }, { type: 'text', text: ' ' });
		}

		return { type: 'doc', content };
	}

	function fromEditorJson(json: JSONContent) {
		if (!json.content) {
			invitations.value = [];
			return;
		}

		invitations.value = json.content
			.filter((node) => isUserInvitation(node))
			.map((node) => ({
				email: node.attrs.email as string,
				recipientType: node.attrs.recipientType as 'existing_member' | 'new_email',
				userId: (node.attrs.userId as string | null) ?? null,
				fullName: (node.attrs.fullName as string | null) ?? null,
			}));
	}

	function getEffectiveRecipients(pendingText: string): UserInvitationAttributes[] {
		const trimmed = pendingText.trim().toLowerCase();

		if (!trimmed || !isValidEmail(trimmed)) {
			return invitations.value;
		}

		const isDuplicate = invitations.value.some((r) => r.email.toLowerCase() === trimmed);

		if (isDuplicate) {
			return invitations.value;
		}

		return [
			...invitations.value,
			{ email: trimmed, recipientType: 'new_email', userId: null, fullName: null },
		];
	}

	return {
		toEditorContent,
		fromEditorJson,
		getEffectiveRecipients,
	};
}
